import { type JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { HelpDrawer } from '@/components/help/help-drawer';
import { Icon } from '@/components/icons';
import { TenantSwitcher } from '@/components/layout/tenant-switcher';
import { UserMenu } from '@/components/layout/user-menu';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { getTheme, setTheme, type Theme } from '@/lib/theme';

const LANG_STORAGE_KEY = 'ezsender:lang';

export function Topbar(): JSX.Element {
  const { t, i18n } = useTranslation('default');

  // TODO(F2.5-C): real notifications + unread count
  const unread = false;

  const currentLang = (i18n.resolvedLanguage ?? i18n.language ?? 'es').slice(0, 2);
  // Local mirror of the persisted theme so toggling re-renders this header.
  // The single source of truth still lives on <html data-theme>, written by
  // `setTheme()`. We just need the React tree to know the value changed.
  const [currentTheme, setCurrentTheme] = useState<Theme>(() => getTheme());

  const [helpOpen, setHelpOpen] = useState(false);

  const toggleLanguage = (): void => {
    const next = currentLang === 'es' ? 'en' : 'es';
    void i18n.changeLanguage(next);
    try {
      window.localStorage.setItem(LANG_STORAGE_KEY, next);
    } catch {
      // Privacy mode / disabled storage — language switch still applies in-memory.
    }
  };

  const toggleTheme = (): void => {
    const next: Theme = currentTheme === 'light' ? 'dark' : 'light';
    setTheme(next);
    setCurrentTheme(next);
  };

  return (
    <header className="flex-shrink-0 h-14 bg-surface border-b border-line flex items-center gap-4 px-6">
      {/* Tenant switcher — only renders for reseller / platform sessions
          (renders null otherwise so customer-tenant users see no shift). */}
      <TenantSwitcher />

      {/* Search — non-functional placeholder. TODO(F2.5-C): functional ⌘K command palette */}
      <div className="flex-1 max-w-[480px] relative">
        <Icon.search
          width="16"
          height="16"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-text-3 pointer-events-none"
        />
        <input
          type="text"
          placeholder={t('search')}
          aria-label={t('search')}
          className={cn(
            'w-full h-9 pl-9 pr-14 text-[13px] rounded-md',
            'bg-surface-2 text-text border border-line outline-none',
            'placeholder:text-text-4 transition-[border-color,box-shadow] duration-100',
            'focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow',
          )}
        />
        <kbd
          aria-hidden="true"
          className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-xs bg-surface border border-line mono text-[11px] text-text-3"
        >
          ⌘K
        </kbd>
      </div>

      <div className="flex-1" />

      {/* Language toggle */}
      <Button
        variant="ghost"
        size="sm"
        onClick={toggleLanguage}
        className="gap-1.5"
        aria-label={`Language: ${currentLang.toUpperCase()}`}
        title="Language"
      >
        <Icon.globe width="15" height="15" />
        <span className="uppercase text-[11px] font-semibold tracking-[0.04em]">
          {currentLang}
        </span>
      </Button>

      {/* Theme toggle */}
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        onClick={toggleTheme}
        aria-label={`Theme: ${currentTheme}`}
        title="Theme"
      >
        {currentTheme === 'light' ? (
          <Icon.moon width="16" height="16" />
        ) : (
          <Icon.sun width="16" height="16" />
        )}
      </Button>

      {/* Help — slide-in drawer with FAQs + search. Full /help page
          available from inside the drawer too. */}
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        onClick={() => setHelpOpen(true)}
        aria-label={t('help_title')}
        title={t('help_title')}
      >
        <Icon.help width="16" height="16" />
      </Button>

      {/* Notifications */}
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        className="relative"
        aria-label="Notifications"
        title="Notifications"
      >
        <Icon.bell width="16" height="16" />
        {unread && (
          <span
            aria-hidden="true"
            className="absolute top-1.5 right-1.5 w-[7px] h-[7px] rounded-full bg-volt border-[1.5px] border-surface"
          />
        )}
      </Button>

      {/* User menu — dropdown with account info, settings link, and
          logout. Replaces the previous single-click logout footgun. */}
      <UserMenu />

      <HelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} />
    </header>
  );
}
